// apps/web/src/features/cargo/cargoFiltersStorage.ts
//
// Pure (no React, no auth store) filter (de)serialization plus the per-browser
// persistence layer for `/cargo` (#1231, #2104). URL search params are the
// primary, shareable surface; `localStorage['magimp:filters:cargo']` is the
// per-browser fallback restored when the page opens at a clean `/cargo` (after
// login or the Cargo sidebar item). Search `q` is URL-only — it is NEVER
// persisted here and NEVER restored from storage. `useCargoFilters` reads and
// writes through these helpers; `auth/userCacheReset.ts` removes the key on
// real identity boundary transitions.

import {
  CargoCarrierPaymentStatusSchema,
  CargoClientPaymentStatusSchema,
  CargoDateTypeSchema,
  CargoLifecycleSchema,
  CargoOverdueBucketSchema,
  type CargoCarrierPaymentStatus,
  type CargoClientPaymentStatus,
  type CargoDateType,
  type CargoLifecycle,
  type CargoOverdueBucket,
} from '@magimp/shared';

/** Hard wire-limit for the article search, mirrored by the input maxLength. */
export const CARGO_SEARCH_MAX_CHARS = 200;

/**
 * @aiPurpose canonical localStorage key for the persisted `/cargo` filter
 *   payload (#2104). Single source — `useCargoFilters` reads/writes through it
 *   and `auth/userCacheReset.ts` removes it on real identity transitions.
 */
export const CARGO_FILTERS_STORAGE_KEY = 'magimp:filters:cargo';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const FILTER_PARAM_KEYS = [
  'q',
  'lifecycle',
  'responsibleManagerId',
  'carrier',
  'pathFormat',
  'clientPaymentStatus',
  'carrierPaymentStatus',
  'dateType',
  'dateFrom',
  'dateTo',
  'weightFrom',
  'weightTo',
  'volumeFrom',
  'volumeTo',
  'totalCostFrom',
  'totalCostTo',
  'overdue',
  'hasRejectedDoc',
  'dtouched',
] as const;

/**
 * Filter params that are NO LONGER active UI filters but must still be scrubbed
 * from the URL by `writeState` (#2162): the old 2-state cargo `status` filter
 * was removed in favor of the 3-state `lifecycle` filter. Kept out of
 * `FILTER_PARAM_KEYS` so a bare legacy `?status=` URL does NOT count as a
 * persisted filter; it is ignored, then cleared on the next filter write.
 */
const LEGACY_FILTER_PARAM_KEYS_TO_CLEAR = ['status'] as const;

/**
 * @aiPurpose URL-canonical filter shape consumed by the list page. Dates are
 *   `YYYY-MM-DD` (the `<input type="date">` surface); the wire-build path
 *   widens them to instant boundaries.
 */
export interface CargoFiltersState {
  q: string;
  lifecycle: CargoLifecycle | null;
  responsibleManagerId: string;
  carrier: string | null;
  /** #2165: exact-match route/transport-format filter (mirrors `carrier`). */
  pathFormat: string | null;
  clientPaymentStatus: CargoClientPaymentStatus | null;
  carrierPaymentStatus: CargoCarrierPaymentStatus | null;
  dateType: CargoDateType;
  dateFrom: string;
  dateTo: string;
  weightFrom: string;
  weightTo: string;
  volumeFrom: string;
  volumeTo: string;
  totalCostFrom: string;
  totalCostTo: string;
  overdue: CargoOverdueBucket | null;
  /** #1939: restrict to cargos with ≥1 active rejected client-payment document. */
  hasRejectedDocument: boolean;
  /**
   * True once the user has deliberately interacted with any date control
   * (set/clear a date, or change the date type). Suppresses the logist
   * default-date window so a cleared range stays unbounded. Persisted to the
   * URL (`dtouched=1`) so the suppression survives refresh and deep-link.
   */
  datesTouched: boolean;
}

function readEnum<T extends string>(
  raw: string | null,
  schema: { safeParse: (v: unknown) => { success: boolean } },
): T | null {
  if (raw === null) return null;
  return schema.safeParse(raw).success ? (raw as T) : null;
}

/** Trimmed free-text param read: a non-empty trimmed value, else null. */
function readNonEmpty(raw: string | null): string | null {
  const v = raw?.trim();
  return v && v.length > 0 ? v : null;
}

export function isCargoRangeBoundActive(value: string): boolean {
  if (value.trim() === '') return false;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0;
}

/** Keep a valid nonnegative number as editable text; invalid URL values are inactive. */
function readRangeBound(raw: string | null): string {
  const value = raw?.trim();
  if (!value) return '';
  return isCargoRangeBoundActive(value) ? value : '';
}

export function readState(sp: URLSearchParams): CargoFiltersState {
  const dateTypeRaw = sp.get('dateType');
  const dateType =
    dateTypeRaw && CargoDateTypeSchema.safeParse(dateTypeRaw).success
      ? (dateTypeRaw as CargoDateType)
      : 'china_ship';
  const responsibleManagerId = sp.get('responsibleManagerId') ?? '';
  const dateFrom = sp.get('dateFrom') ?? '';
  const dateTo = sp.get('dateTo') ?? '';
  return {
    q: (sp.get('q') ?? '').slice(0, CARGO_SEARCH_MAX_CHARS),
    lifecycle: readEnum<CargoLifecycle>(sp.get('lifecycle'), CargoLifecycleSchema),
    responsibleManagerId: UUID_RE.test(responsibleManagerId) ? responsibleManagerId : '',
    carrier: readNonEmpty(sp.get('carrier')),
    pathFormat: readNonEmpty(sp.get('pathFormat')),
    clientPaymentStatus: readEnum<CargoClientPaymentStatus>(
      sp.get('clientPaymentStatus'),
      CargoClientPaymentStatusSchema,
    ),
    carrierPaymentStatus: readEnum<CargoCarrierPaymentStatus>(
      sp.get('carrierPaymentStatus'),
      CargoCarrierPaymentStatusSchema,
    ),
    dateType,
    dateFrom: DATE_RE.test(dateFrom) ? dateFrom : '',
    dateTo: DATE_RE.test(dateTo) ? dateTo : '',
    weightFrom: readRangeBound(sp.get('weightFrom')),
    weightTo: readRangeBound(sp.get('weightTo')),
    volumeFrom: readRangeBound(sp.get('volumeFrom')),
    volumeTo: readRangeBound(sp.get('volumeTo')),
    totalCostFrom: readRangeBound(sp.get('totalCostFrom')),
    totalCostTo: readRangeBound(sp.get('totalCostTo')),
    overdue: readEnum<CargoOverdueBucket>(sp.get('overdue'), CargoOverdueBucketSchema),
    hasRejectedDocument: sp.get('hasRejectedDoc') === '1',
    datesTouched: sp.get('dtouched') === '1',
  };
}

export function writeState(prev: URLSearchParams, next: CargoFiltersState): URLSearchParams {
  const out = new URLSearchParams(prev);
  for (const k of FILTER_PARAM_KEYS) out.delete(k);
  for (const k of LEGACY_FILTER_PARAM_KEYS_TO_CLEAR) out.delete(k);
  const q = next.q.trim().slice(0, CARGO_SEARCH_MAX_CHARS);
  if (q) out.set('q', q);
  if (next.lifecycle) out.set('lifecycle', next.lifecycle);
  if (next.responsibleManagerId) out.set('responsibleManagerId', next.responsibleManagerId);
  if (next.carrier) out.set('carrier', next.carrier);
  if (next.pathFormat) out.set('pathFormat', next.pathFormat);
  if (next.clientPaymentStatus) out.set('clientPaymentStatus', next.clientPaymentStatus);
  if (next.carrierPaymentStatus) out.set('carrierPaymentStatus', next.carrierPaymentStatus);
  // dateType is only canonical alongside a chosen range — see wire-build doc.
  if (next.dateFrom || next.dateTo) out.set('dateType', next.dateType);
  if (next.dateFrom) out.set('dateFrom', next.dateFrom);
  if (next.dateTo) out.set('dateTo', next.dateTo);
  if (isCargoRangeBoundActive(next.weightFrom)) out.set('weightFrom', next.weightFrom);
  if (isCargoRangeBoundActive(next.weightTo)) out.set('weightTo', next.weightTo);
  if (isCargoRangeBoundActive(next.volumeFrom)) out.set('volumeFrom', next.volumeFrom);
  if (isCargoRangeBoundActive(next.volumeTo)) out.set('volumeTo', next.volumeTo);
  if (isCargoRangeBoundActive(next.totalCostFrom)) out.set('totalCostFrom', next.totalCostFrom);
  if (isCargoRangeBoundActive(next.totalCostTo)) out.set('totalCostTo', next.totalCostTo);
  if (next.overdue) out.set('overdue', next.overdue);
  if (next.hasRejectedDocument) out.set('hasRejectedDoc', '1');
  // Omit when false so a fresh/untouched URL stays clean.
  if (next.datesTouched) out.set('dtouched', '1');
  return out;
}

/**
 * True when the URL carries any persisted filter key EXCEPT `q`. `q` is
 * URL-only (live search), so a bare `?q=...` must NOT make the URL
 * authoritative over a stored payload during hydration (#2104).
 */
export function hasPersistedCargoFilterParam(sp: URLSearchParams): boolean {
  for (const k of FILTER_PARAM_KEYS) {
    if (k === 'q') continue;
    if (sp.has(k)) return true;
  }
  return false;
}

export function safeReadCargoFilters(): string | null {
  try {
    return localStorage.getItem(CARGO_FILTERS_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function safeWriteCargoFilters(value: string): void {
  try {
    if (value === '') {
      localStorage.removeItem(CARGO_FILTERS_STORAGE_KEY);
    } else {
      localStorage.setItem(CARGO_FILTERS_STORAGE_KEY, value);
    }
  } catch {
    // best-effort
  }
}

/**
 * Persisted filter payload (the canonical query string sans `q`). `dtouched=1`
 * IS retained — it is a meaningful persisted value that keeps the logist
 * default-date window suppressed after one date interaction.
 */
export function buildPersistedCargoQuery(state: CargoFiltersState): string {
  const p = writeState(new URLSearchParams(), state);
  p.delete('q');
  return p.toString();
}
